import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Story } from '../../types/commerce';
import './Stories.css';

export default function StoryPage() {
  const { slug = '' } = useParams();
  const query = useApiQuery<Story>(`/commerce/stories/${encodeURIComponent(slug)}`);
  const heading = useRef<HTMLHeadingElement>(null);
  const focused = useRef<string | undefined>(undefined);
  useDocumentTitle(query.data?.title ?? 'Story');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  useEffect(() => {
    if (!query.loading && focused.current !== slug) {
      heading.current?.focus({ preventScroll: true });
      focused.current = slug;
    }
  }, [query.loading, slug]);
  const story = query.data;
  return (
    <article className="story-page page-container">
      <Link to="/stories">All stories</Link>
      {query.loading ? (
        <p role="status">Loading story…</p>
      ) : query.errorStatus === 404 ? (
        <h1 ref={heading} tabIndex={-1}>
          Story not found
        </h1>
      ) : query.error ? (
        <div role="alert">
          <h1 ref={heading} tabIndex={-1}>
            Could not load this story
          </h1>
          <button onClick={query.reload}>Try again</button>
        </div>
      ) : (
        story && (
          <>
            <h1 ref={heading} tabIndex={-1}>
              {story.title}
            </h1>
            {story.published_at && (
              <time dateTime={story.published_at}>
                {new Date(story.published_at).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </time>
            )}
            <img className="story-page__cover" src={story.cover_image_url} alt="" />
            <div className="story-page__body">
              {story.body
                .split(/\r?\n\s*\r?\n/)
                .filter(Boolean)
                .map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
            </div>
            {!!story.links.length && (
              <ul>
                {story.links.map((link, index) => (
                  <li key={index}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </>
        )
      )}
    </article>
  );
}
